import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {
  confirmVerification,
  fetchAccountProfile,
  sendVerification,
  updateAccountProfile,
} from '@/entities/account/api';
import type {VerifyChannel} from '@/entities/account/types';
import {useAuthStore} from '@/entities/session/auth-store';
import {ApiError} from '@/shared/api';
import {
  currentLang,
  LANG_LABELS,
  setLang,
  SUPPORTED_LANGS,
  type Lang,
} from '@/shared/config/i18n';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {FormRow} from '@/shared/ui/form';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';
import {THEME_MODES, useThemeMode, useThemeModeStore} from '@/shared/ui/theme-mode';

/**
 * The account card — the person, not any company they belong to.
 *
 * `GET /auth/profile` is a wider read than the `/auth/me` the session resolver uses:
 * it carries the verification flags and `has_password`, which is what makes a
 * security page possible at all (see SecurityScreen — an account created through a
 * social login has no current password to prove, and needs `set-password` instead of
 * `change-password`).
 *
 * Verification is two calls and a code, so it lives in a sheet rather than inline: an
 * email field that silently becomes a code field is a form that has changed meaning
 * under the person filling it in.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'Account'>;

const styles = StyleSheet.create({
  card: {gap: spacing(3)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rowText: {flex: 1, gap: 2},
  sheet: {gap: spacing(3)},
});

export function AccountScreen(_: Props) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const account = useAuthStore(s => s.account);
  // Local state alongside i18n's own: `changeLanguage` re-renders through the
  // useTranslation hook, but the CHIPS need the selected value on the same tick, and
  // reading `i18n.resolvedLanguage` during render would lag one frame behind the tap.
  const [lang, setLangState] = useState<Lang>(currentLang);
  const themeMode = useThemeMode();
  const setThemeMode = useThemeModeStore(s => s.setMode);

  const profile = useQuery({
    queryKey: ['account-profile'],
    queryFn: fetchAccountProfile,
  });

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [timezone, setTimezone] = useState('');
  const [saved, setSaved] = useState(false);

  // Seeded from the server ONCE it arrives, and never again: re-seeding on every
  // refetch would wipe what the user is halfway through typing.
  useEffect(() => {
    if (!profile.data) {
      return;
    }
    setFullName(profile.data.full_name);
    setUsername(profile.data.username);
    setTimezone(profile.data.timezone);
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () =>
      updateAccountProfile({
        full_name: fullName,
        username,
        timezone,
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({queryKey: ['account-profile']});
    },
  });

  const [verifying, setVerifying] = useState<VerifyChannel | null>(null);
  const [code, setCode] = useState('');
  const [newValue, setNewValue] = useState('');

  const startVerify = useMutation({
    mutationFn: (channel: VerifyChannel) =>
      sendVerification(channel, newValue.trim() || undefined),
  });

  const finishVerify = useMutation({
    mutationFn: (channel: VerifyChannel) => confirmVerification(channel, code.trim()),
    onSuccess: async () => {
      setVerifying(null);
      setCode('');
      setNewValue('');
      await qc.invalidateQueries({queryKey: ['account-profile']});
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="account-screen"
      title={t('more.accountProfile')}
      >

      {profile.isPending ? (
        <Skeleton height={220} radius={radii.lg} />
      ) : profile.isError ? (
        <Banner tone="error" title={t('errors.generic')} />
      ) : (
        <>
          <Section title={t('profile.account')}>
            <Card padding={4} style={styles.card}>
              <FormRow>
                <AppInput
                  label={t('profile.fullName')}
                  value={fullName}
                  onChangeText={value => {
                    setFullName(value);
                    setSaved(false);
                  }}
                  testID="account-full-name"
                />
                <AppInput
                  label={t('account.username')}
                  value={username}
                  onChangeText={value => {
                    setUsername(value);
                    setSaved(false);
                  }}
                  autoCapitalize="none"
                  testID="account-username"
                />
              </FormRow>
              <FormRow>
                <AppInput
                  label={t('account.timezone')}
                  value={timezone}
                  onChangeText={value => {
                    setTimezone(value);
                    setSaved(false);
                  }}
                  autoCapitalize="none"
                  hint={t('account.timezoneHint')}
                  testID="account-timezone"
                />
              </FormRow>
              {save.isError ? (
                <Banner tone="error" title={errorOf(save.error)} />
              ) : null}
              {saved ? <Banner tone="success" title={t('account.saved')} /> : null}
              <AppButton
                title={t('actions.save')}
                loading={save.isPending}
                onPress={() => save.mutate()}
                testID="account-save"
              />
            </Card>
          </Section>

          <Section title={t('account.contacts')} hint={t('account.contactsHint')}>
            <Card padding={4} style={styles.card}>
              <View style={styles.row}>
                <View style={styles.rowText}>
                  <AppText variant="subtitle">
                    {profile.data.email || '—'}
                  </AppText>
                  <AppText variant="caption">{t('profile.email')}</AppText>
                </View>
                <StatusPill
                  tone={profile.data.email_verified ? 'granted' : 'pending'}
                  size="sm"
                  label={t(
                    profile.data.email_verified
                      ? 'account.verified'
                      : 'account.unverified',
                  )}
                />
              </View>
              <AppButton
                title={t(
                  profile.data.email_verified
                    ? 'account.changeEmail'
                    : 'account.verifyEmail',
                )}
                tone="soft"
                size="sm"
                onPress={() => {
                  setVerifying('email');
                  setNewValue('');
                  setCode('');
                }}
                testID="account-verify-email"
              />

              <View style={styles.row}>
                <View style={styles.rowText}>
                  <AppText variant="subtitle">
                    {profile.data.phone || t('account.noPhone')}
                  </AppText>
                  <AppText variant="caption">{t('account.phone')}</AppText>
                </View>
                <StatusPill
                  tone={profile.data.phone_verified ? 'granted' : 'neutral'}
                  size="sm"
                  label={t(
                    profile.data.phone_verified
                      ? 'account.verified'
                      : 'account.unverified',
                  )}
                />
              </View>
              <AppButton
                title={t(
                  profile.data.phone ? 'account.changePhone' : 'account.addPhone',
                )}
                tone="neutral"
                size="sm"
                onPress={() => {
                  setVerifying('phone');
                  setNewValue('');
                  setCode('');
                }}
                testID="account-verify-phone"
              />
            </Card>
          </Section>

          {/* Language belongs on the ACCOUNT card, not under company settings: it is a
              property of the person reading the screen, and it follows them across
              every context they switch into. Stored locally rather than on the server —
              nothing in the API carries a UI language, and the phone is where the
              preference is actually exercised. */}
          <Section title={t('account.language')} hint={t('account.languageHint')}>
            <ChoiceChips
              options={SUPPORTED_LANGS.map(code => ({
                value: code,
                // Each language names itself, in itself — a Russian speaker looking for
                // «Русский» in an English list should not have to know the word.
                label: LANG_LABELS[code],
              }))}
              value={lang}
              onChange={next => {
                setLangState(next);
                void setLang(next);
              }}
              testID="account-language"
            />
          </Section>

          {/* Appearance sits beside language for the same reason: both are properties
              of the person, not of any company, and both follow them into every
              context. `system` is the default and stays first — deferring to the device
              is what most people want, and the other two exist for the ones the device
              gets wrong (a night shift under floodlights, a phone nobody ever set). */}
          <Section title={t('account.theme')} hint={t('account.themeHint')}>
            <ChoiceChips
              options={THEME_MODES.map(mode => ({
                value: mode,
                label: t(`account.themeMode.${mode}`),
              }))}
              value={themeMode}
              onChange={setThemeMode}
              testID="account-theme"
            />
          </Section>

          <Section title={t('account.identifiers')}>
            <Card padding={4} style={styles.card}>
              <AppText variant="mono">{account?.id ?? profile.data.id}</AppText>
              <AppText variant="caption">{t('profile.accountId')}</AppText>
            </Card>
          </Section>
        </>
      )}

      <Sheet
        visible={verifying !== null}
        onClose={() => setVerifying(null)}
        title={t(verifying === 'phone' ? 'account.phone' : 'profile.email')}
        subtitle={t('account.verifyHint')}
        testID="account-verify-sheet">
        <View style={styles.sheet}>
          <AppInput
            label={t('account.newValue')}
            value={newValue}
            onChangeText={setNewValue}
            autoCapitalize="none"
            keyboardType={verifying === 'phone' ? 'phone-pad' : 'email-address'}
            hint={t('account.newValueHint')}
            testID="account-new-value"
          />
          <AppButton
            title={t('account.sendCode')}
            tone="soft"
            loading={startVerify.isPending}
            onPress={() => verifying && startVerify.mutate(verifying)}
            testID="account-send-code"
          />
          {startVerify.isError ? (
            <Banner tone="error" title={errorOf(startVerify.error)} />
          ) : null}
          {startVerify.isSuccess ? (
            <Banner tone="success" title={t('account.codeSent')} />
          ) : null}

          <AppInput
            label={t('account.code')}
            value={code}
            onChangeText={setCode}
            keyboardType="number-pad"
            testID="account-code"
          />
          {finishVerify.isError ? (
            <Banner tone="error" title={errorOf(finishVerify.error)} />
          ) : null}
          <AppButton
            title={t('actions.confirm')}
            disabled={code.trim().length === 0}
            loading={finishVerify.isPending}
            onPress={() => verifying && finishVerify.mutate(verifying)}
            testID="account-confirm-code"
          />
        </View>
      </Sheet>
    </Page>
  );
}
